export default {
  name: {
    id: 'rm_metal_rare_earth_name',
    defaultMessage: 'Rare Earth Elements',
  },
  smartphoneLabel: {
    id: 'rm_metal_rare_earth_smartphone_label',
    defaultMessage: 'Trace (invisible to the naked eye)',
  },
  whatItDoesText: {
    id: 'rm_metal_rare_earth_what_it_does',
    defaultMessage:
      "Rare Earth materials are why your screen looks like a window instead of a light bulb, why your camera locks focus instantly, and why your phone buzzes when you tap it. A family of 17 elements[^*], they are prized for their magnetic strength, light emissions, and structural resilience. Without them, there's no sharp image, no true colour, or no responsive feel on your smartphones; you'd just have a rectangle of glass that doesn't quite work.",
  },
  extractionText1: {
    id: 'rm_metal_rare_earth_extraction',
    defaultMessage:
      'Rare Earth Elements (REEs) are not actually rare in the Earth\'s crust. They are called "rare" because they exist at very low concentrations in rock, often co-extracted alongside iron, titanium, and tin. Two extraction methods are used: the first is open-air leaching ponds with chemicals; the second injects chemicals directly underground through pipes. The latter is often used for heavy REEs in southern China\'s clay deposits. Both leave toxic residues in the ground, including one tonne of radioactive waste per tonne of metal produced, making REE processing one of the most chemically intensive operations in mining.',
  },
  quickFactText: {
    id: 'rm_metal_rare_earth_quick_fact',
    defaultMessage:
      'For every ton of rare earth metals extracted, approximately one ton of radioactive waste is generated and left behind.',
  },
  envSummaryText: {
    id: 'rm_metal_rare_earth_env_summary',
    defaultMessage:
      'Rare earth extraction has one of the highest waste-to-metal ratios in mining: around 2,000 tons of waste are generated per ton of rare earth elements produced, including radioactive by-products. At Bayan Obo in Inner Mongolia, thorium co-extracted with rare earth ores has generated extensive radioactive tailings. With most production concentrated in China, where environmental regulation has historically been weak, local soil, water and ecosystems face severe, long-lasting impacts.',
  },
  envWaterText: {
    id: 'rm_metal_rare_earth_env_water_text',
    defaultMessage:
      'Rare earth extraction uses large quantities of acids and solvents, which can leak into groundwater and contaminate water sources used by local communities. Water consumption is also growing: water use reached around 111 million gallons in 2010 and is projected to more than double to 241 million gallons by 2030, driven mainly by neodymium and cerium.',
  },
  envSoilText: {
    id: 'rm_metal_rare_earth_env_soil_text',
    defaultMessage:
      'Producing one ton of rare earth elements generates around 2,000 tons of waste, including roughly one ton of radioactive residue. This radioactivity comes from thorium and uranium naturally present in rare earth ores and concentrated during processing. In southern China, heavy rare earth elements are extracted through ion-adsorption clay mining, which uses ammonium sulfate solutions to leach rare earths from clay, damaging soil structure and leaving large areas difficult to remediate.',
  },
  envClimateText: {
    id: 'rm_metal_rare_earth_env_climate_text',
    defaultMessage:
      'Processing REEs requires large quantities of highly corrosive chemicals, including hydrochloric acid and ammonium compounds, of which the production itself consumes significant fossil fuels. Globally, mineral and metal production accounts for about 10% of total energy-related greenhouse gas emissions.',
  },
  humanSummaryText: {
    id: 'rm_metal_rare_earth_human_summary',
    defaultMessage:
      'Rare earth elements are extracted in countries where environmental and human impacts are often poorly documented and, in some cases, permanent. Some REE mining regions carry significant and well-documented social burdens. The communities that bear these costs are often geographically and politically distant from the consumers who benefit from the technologies these metals enable.',
  },
  humanHealthText: {
    id: 'rm_metal_rare_earth_human_health_text',
    defaultMessage:
      'REE mining can expose communities to acidic drainage, radionuclides and heavy metals, contaminating drinking water, farmland and air. In Jiangxi, decades of ion-adsorption leaching have been associated with water contamination, crop failures and elevated cancer rates. Experts estimate environmental recovery could take 50 to 100 years, beyond the lifetime of many residents. In Bayan Obo, communities face radioactive dust and contaminated water from tailings facilities.',
  },
  humanLegalText: {
    id: 'rm_metal_rare_earth_human_legal_text',
    defaultMessage:
      'In Myanmar’s Kachin State, REE mining, including dysprosium and terbium extraction for use in smartphones, is largely controlled by militias and operates with limited government oversight. More than 200 unlicensed sites reportedly supply exports to China, while local communities bear the environmental costs and receive little of the economic benefit. In Jiangxi, limited legal recourse and weak monitoring leave affected communities with few effective protections.',
  },
  geoSummaryText: {
    id: 'rm_metal_rare_earth_geo_summary',
    defaultMessage:
      'The geopolitical risk lies not in who mines the ore, but in who controls the chemistry of separation. China has built processing dominance over 30 years, a capacity that would take an estimated 10 to 15 years to replicate elsewhere. Even ore mined in Australia or Myanmar is sent to China for final processing. In April 2025, China used this leverage to impose immediate export controls on seven rare-earth elements, triggering price spikes.',
  },
  geoDugText: {
    id: 'rm_metal_rare_earth_geo_dug_text',
    defaultMessage:
      'China mines nearly 70% of the world’s rare earths. Myanmar supplies a significant share of the heaviest and most magnet-critical elements, while Australia is one of the few countries with a major mining operation outside Chinese control. Rare-earth mining is therefore among the most geographically concentrated of any critical-material supply chain.',
  },
  geoProcessedText: {
    id: 'rm_metal_rare_earth_geo_processed_text',
    defaultMessage:
      'Much of the rare earth material mined outside China is processed there. China refines more than 90% of global output. Australia’s leading producer cannot yet fully process the most critical elements domestically, while Myanmar sends its ore to China. By 2035, China is projected to supply around 80% of global rare earths.',
  },
  geoLeverageText: {
    id: 'rm_metal_rare_earth_geo_leverage_text',
    defaultMessage:
      'In April 2025, China imposed immediate export controls on seven key rare earth elements used in electric motors, defence systems, and wind turbines. It was not the first time: in 2010, China restricted exports and prices spiked up to tenfold. Rare earths are among the three most price-volatile strategic minerals in the world, behind only lithium and vanadium.',
  },
  supplySummaryText: {
    id: 'rm_metal_rare_earth_supply_summary',
    defaultMessage:
      'Rare earth elements are not particularly rare in the Earth’s crust, but they are extraordinarily difficult to extract and separate, both from one another and from naturally occurring radioactive materials. Building processing capacity takes many years, requires specialised expertise and generates significant toxic waste. The supply chain was fragile long before it became a geopolitical tool.',
  },
  supplyReservesText: {
    id: 'rm_metal_rare_earth_supply_reserves_text',
    defaultMessage:
      'Physical reserves are broadly adequate, so the world is not running out of rare earth elements. However, not all REEs are equally abundant: dysprosium and terbium, which are used in high-performance magnets, are scarcer and harder to source outside a handful of deposits. Even where the geology exists, accessing and processing these deposits remains challenging.',
  },
  supplyDemandText: {
    id: 'rm_metal_rare_earth_supply_demand_text',
    defaultMessage:
      'Total demand for the four magnet rare earths, neodymium, praseodymium, dysprosium and terbium, is projected to grow from 93 kt in 2025 to 167 kt in 2050, an increase of about 80%. Clean-energy applications account for 22% of demand in 2025 and 34% by 2050. Existing and announced projects are expected to cover primary supply requirements, with no shortfall shown through 2040.',
  },
  recyclingSummaryText: {
    id: 'rm_metal_rare_earth_recycling_summary',
    defaultMessage:
      'Rare earths are the most urgent recycling priority and the biggest current failure. Today, most recycling happens at the factory, not at end of life: manufacturers recover offcuts from magnet production, but almost nothing is recovered from old motors or wind turbines. End-of-life collection rates from permanent magnets remain below 15%, because the magnets are tiny, embedded in complex products, and costly to extract.',
  },
  recyclingInputText: {
    id: 'rm_metal_rare_earth_recycling_input_text',
    defaultMessage:
      'Very little and the reason is timing, not technology. An electric vehicle motor contains around 1 kg of rare earth magnets; a single wind turbine, up to 2 tonnes. These will become the main source of recyclable rare earths but most are still in active use. The recycling feedstock of the future is currently powering roads and electricity grids.',
  },
  recyclingOutlookText: {
    id: 'rm_metal_rare_earth_recycling_outlook_text',
    defaultMessage:
      'Partly, but not enough on its own. Secondary supply could triple by 2050, covering more than 30% of demand. That still leaves 70% to be sourced from primary mining. Closing the gap also needs economic incentives, mandatory targets, and binding commitments from manufacturers. The EU has set a target of 25% recycled rare earths by 2030 without a detailed plan to get there.',
  },
  footnote1Text1: {
    id: 'rm_metal_rare_earth_footnote_1_p1',
    defaultMessage:
      'Rare earth elements (REEs) are a family of 17 chemical elements: lanthanum (La), cerium (Ce), praseodymium (Pr), neodymium (Nd), promethium (Pm), samarium (Sm), europium (Eu), gadolinium (Gd), terbium (Tb), dysprosium (Dy), holmium (Ho), erbium (Er), thulium (Tm), ytterbium (Yb), lutetium (Lu), scandium (Sc), and yttrium (Y).',
  },
  footnote1Text2: {
    id: 'rm_metal_rare_earth_footnote_1_p2',
    defaultMessage:
      'REEs are conventionally divided into two groups based on their atomic weight and extraction characteristics. Light rare earth elements (LREEs) include La, Ce, Pr, Nd, Pm, and Sm. Heavy rare earth elements (HREEs) include Eu, Gd, Tb, Dy, Ho, Er, Tm, Yb, Lu, and Y. HREEs are generally scarcer, more critical, and more difficult to process than LREEs. Scandium is sometimes classified separately due to its distinct properties.',
  },
  footnote2Text1: {
    id: 'rm_metal_rare_earth_footnote_2_p1',
    defaultMessage:
      'Each REE has a distinct extraction process, supply chain, and set of impacts. For readability, this sheet displays the highest score recorded across the family. The full breakdown by element is as follows:',
  },
  footnote2Text2: {
    id: 'rm_metal_rare_earth_footnote_2_p2',
    defaultMessage:
      'Environmental Impact: Standard (1/5): Ce, La, Nd, Pr, Y; Significant (2/5): Sm, Dy, Er, Gd, Yb; High (3/5): Eu, Lu, Sc, Tb, Tm',
  },
  footnote2Text3: {
    id: 'rm_metal_rare_earth_footnote_2_p3',
    defaultMessage:
      'Human Impact: Moderate (2/5): Ce, La, Nd, Pr, Sm, Dy, Eu, Gd, Sc, Tb, Y; Significant (3/5): Er, Lu, Tm, Yb',
  },
  footnote2Text4: {
    id: 'rm_metal_rare_earth_footnote_2_p4',
    defaultMessage:
      'Geopolitical Impact: Elevated (3/5): Ce, La, Nd, Pr, Dy, Y; High (4/5): Sm, Er, Eu, Gd, Lu, Sc, Tb, Tm, Yb',
  },
  mine0Region: {
    id: 'rm_metal_rare-earth_mine0_region',
    defaultMessage: 'Baotou',
  },
  mine1Region: {
    id: 'rm_metal_rare-earth_mine1_region',
    defaultMessage: 'Mountain Pass',
  },
  mine2Region: {
    id: 'rm_metal_rare-earth_mine2_region',
    defaultMessage: 'Laverton',
  },
  mine3Region: {
    id: 'rm_metal_rare-earth_mine3_region',
    defaultMessage: 'Ganzhou',
  },
  mine4Region: {
    id: 'rm_metal_rare-earth_mine4_region',
    defaultMessage: 'Longnan',
  },
  mine5Region: {
    id: 'rm_metal_rare-earth_mine5_region',
    defaultMessage: 'Myitkyina',
  },
} as const
